import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@material-ui/core';
import { SectionHeader, MetricBox, formatDouble, long } from '../../components/utils';
const ReactHighcharts = require('react-highcharts');

const FloorBanditComponent = ({ authProvider, dateRange = 7, ...props }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const baseUrl = `${window.nobid_endpoint}/floor_bandit_report`;
        const token = localStorage.getItem('token');
        const response = await fetch(`${baseUrl}?show=experiment&show=date&date_range=${dateRange}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const result = await response.json();
        if (result && result.data) {
          setData(result.data);
          setSummary(computeSummary(result.data));
        } else {
          setData([]);
          setSummary(null);
        }
      } catch (e) {
        console.error('FloorBandit fetch error:', e);
        setData([]);
        setSummary(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dateRange]);

  const computeSummary = (records) => {
    const control = { bid_requests: 0, impressions: 0, revenue: 0, net_revenue: 0 };
    const test = { bid_requests: 0, impressions: 0, revenue: 0, net_revenue: 0 };
    const daily = {};

    records.forEach(rec => {
      const isControl = rec.experiment === 'control';
      const bucket = isControl ? control : test;
      bucket.bid_requests += rec.bid_requests || 0;
      bucket.impressions += rec.impressions || 0;
      bucket.revenue += rec.revenue || 0;
      bucket.net_revenue += rec.net_revenue || 0;

      // Daily aggregation for time series
      const date = rec.date;
      if (date) {
        if (!daily[date]) daily[date] = { control: { revenue: 0, bid_requests: 0, impressions: 0 }, test: { revenue: 0, bid_requests: 0, impressions: 0 } };
        const dayBucket = isControl ? daily[date].control : daily[date].test;
        dayBucket.revenue += rec.revenue || 0;
        dayBucket.bid_requests += rec.bid_requests || 0;
        dayBucket.impressions += rec.impressions || 0;
      }
    });

    const controlRPR = control.bid_requests > 0 ? 1000000 * control.revenue / control.bid_requests : 0;
    const testRPR = test.bid_requests > 0 ? 1000000 * test.revenue / test.bid_requests : 0;
    const rprLift = controlRPR > 0 ? (testRPR - controlRPR) / controlRPR : 0;

    const controlCPM = control.impressions > 0 ? 1000 * control.revenue / control.impressions : 0;
    const testCPM = test.impressions > 0 ? 1000 * test.revenue / test.impressions : 0;
    const cpmLift = controlCPM > 0 ? (testCPM - controlCPM) / controlCPM : 0;

    const controlFill = control.bid_requests > 0 ? 100 * control.impressions / control.bid_requests : 0;
    const testFill = test.bid_requests > 0 ? 100 * test.impressions / test.bid_requests : 0;

    return {
      control, test, controlRPR, testRPR, rprLift,
      controlCPM, testCPM, cpmLift,
      controlFill, testFill,
      daily
    };
  };

  const rprChartConfig = useMemo(() => {
    if (!summary) return {};
    return {
      chart: { type: 'column', height: 280, backgroundColor: 'transparent' },
      title: { text: 'RPM - BR', style: { color: '#ffffff', fontSize: '14px' } },
      xAxis: { categories: ['Control', 'Test (All Buckets)'], labels: { style: { color: '#aaaaaa' } } },
      yAxis: { title: { text: 'RPM - BR', style: { color: '#aaaaaa' } }, labels: { style: { color: '#aaaaaa' } } },
      series: [{
        name: 'RPR',
        data: [
          { y: summary.controlRPR, color: '#90a4ae' },
          { y: summary.testRPR, color: summary.rprLift >= 0 ? '#81c784' : '#e57373' }
        ],
        showInLegend: false
      }],
      tooltip: { pointFormat: '<b>${point.y:.2f}</b>' },
      credits: { enabled: false }
    };
  }, [summary]);

  const timeSeriesConfig = useMemo(() => {
    if (!summary || !summary.daily) return {};
    const dates = Object.keys(summary.daily).sort();
    const controlData = dates.map(d => {
      const day = summary.daily[d].control;
      return day.bid_requests > 0 ? 1000000 * day.revenue / day.bid_requests : 0;
    });
    const testData = dates.map(d => {
      const day = summary.daily[d].test;
      return day.bid_requests > 0 ? 1000000 * day.revenue / day.bid_requests : 0;
    });

    return {
      chart: { type: 'line', height: 280, backgroundColor: 'transparent' },
      title: { text: 'RPM - BR Over Time (Test vs Control)', style: { color: '#ffffff', fontSize: '14px' } },
      xAxis: { categories: dates, labels: { style: { color: '#aaaaaa' }, rotation: -45 } },
      yAxis: { title: { text: 'RPM - BR', style: { color: '#aaaaaa' } }, labels: { style: { color: '#aaaaaa' } } },
      series: [
        { name: 'Control', data: controlData, color: '#90a4ae' },
        { name: 'Test', data: testData, color: '#4fc3f7' }
      ],
      legend: { itemStyle: { color: '#aaaaaa' } },
      tooltip: { shared: true, pointFormat: '{series.name}: <b>${point.y:.2f}</b><br/>' },
      credits: { enabled: false }
    };
  }, [summary]);

  if (loading) {
    return (
      <Card raised>
        <CardContent>
          <div style={{ color: '#ffffff' }}>Loading Floor Bandit data...</div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card raised>
        <CardContent>
          <div style={{ color: '#aaaaaa' }}>No Floor Bandit experiment data available.</div>
        </CardContent>
      </Card>
    );
  }

  const liftColor = (lift) => lift >= 0.02 ? '#81c784' : lift <= -0.02 ? '#e57373' : '#ffb74d';
  const liftLabel = (lift) => `${lift >= 0 ? '+' : ''}${(lift * 100).toFixed(2)}%`;
  const verdict = summary.rprLift >= 0.02 ? 'WINNING' : summary.rprLift <= -0.02 ? 'LOSING' : 'NEUTRAL';
  const verdictColor = summary.rprLift >= 0.02 ? '#81c784' : summary.rprLift <= -0.02 ? '#e57373' : '#ffb74d';

  return (
    <div>
      <Card raised style={{ marginBottom: 16 }}>
        <CardContent>
          <SectionHeader title="ML Floor Bandit - Test vs Control" />
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
            <MetricBox label="Verdict">
              <div style={{ color: verdictColor, fontWeight: 'bold', fontSize: '20px', lineHeight: '28px' }}>{verdict}</div>
            </MetricBox>
            <MetricBox label="RPM-BR Lift">
              <div style={{ color: liftColor(summary.rprLift), fontWeight: 'bold', fontSize: '20px', lineHeight: '28px' }}>{liftLabel(summary.rprLift)}</div>
            </MetricBox>
            <MetricBox label="CPM Lift">
              <div style={{ color: liftColor(summary.cpmLift), fontWeight: 'bold', fontSize: '20px', lineHeight: '28px' }}>{liftLabel(summary.cpmLift)}</div>
            </MetricBox>
            <MetricBox label="Control RPM-BR">
              <div style={{ fontSize: '20px', fontWeight: 'bold', lineHeight: '28px' }}>{formatDouble(summary.controlRPR, '$', 2)}</div>
            </MetricBox>
            <MetricBox label="Test RPM-BR">
              <div style={{ fontSize: '20px', fontWeight: 'bold', lineHeight: '28px' }}>{formatDouble(summary.testRPR, '$', 2)}</div>
            </MetricBox>
            <MetricBox label="Control Fill">
              <div style={{ fontSize: '20px', fontWeight: 'bold', lineHeight: '28px' }}>{summary.controlFill.toFixed(2)}%</div>
            </MetricBox>
            <MetricBox label="Test Fill">
              <div style={{ fontSize: '20px', fontWeight: 'bold', lineHeight: '28px' }}>{summary.testFill.toFixed(2)}%</div>
            </MetricBox>
            <MetricBox label="Test Impressions">
              <div style={{ fontSize: '20px', fontWeight: 'bold', lineHeight: '28px' }}>{long(summary.test.impressions)}</div>
            </MetricBox>
          </div>
        </CardContent>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <Card raised>
          <CardContent>
            <ReactHighcharts config={rprChartConfig} isPureConfig={true} />
          </CardContent>
        </Card>
        <Card raised>
          <CardContent>
            <ReactHighcharts config={timeSeriesConfig} isPureConfig={true} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FloorBanditComponent;
